import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Box, Modal, Typography, useTheme } from '@mui/material';
import { Button } from '@components/index';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { XIcon } from '@icons/index';
import { useUpdateTenantConfig } from '@queryHooks/useTenantConfigs';
import { getErrorMessage } from '@locales/i18nErrorMessage';

interface DuplicateLoginModalProps {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  duplicateLoginEnabled: boolean;
}

export const DuplicateLoginModal = ({ open, tenantId, onClose, duplicateLoginEnabled }: DuplicateLoginModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    uiStore: { toastStore },
  } = useStore();
  const [selectedOption, setSelectedOption] = useState<'enabled' | 'disabled'>(
    duplicateLoginEnabled ? 'enabled' : 'disabled',
  );

  const { mutate: updateTenantConfig, isPending } = useUpdateTenantConfig();

  useEffect(() => {
    if (open) {
      setSelectedOption(duplicateLoginEnabled ? 'enabled' : 'disabled');
    }
  }, [open, duplicateLoginEnabled]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    updateTenantConfig(
      {
        tenantId,
        configKey: 'PREVENT_CONCURRENT_LOGIN',
        configValue: selectedOption === 'enabled' ? 'true' : 'false',
      },
      {
        onSuccess: () => {
          toastStore.open({
            message: t('Security.duplicateLogin.modalSuccess'),
          });
          onClose();
        },
        onError: error => {
          toastStore.open({
            message: getErrorMessage(error).message || t('Security.duplicateLogin.modalError'),
          });
        },
      },
    );
  };

  const handleOptionClick = (option: 'enabled' | 'disabled') => {
    setSelectedOption(option);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        <DialogHeader>
          <HeaderTitle variant="headline6">{t('Security.duplicateLogin.modalTitle')}</HeaderTitle>
          <CloseButton onClick={handleClose}>
            <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
          </CloseButton>
        </DialogHeader>

        <DialogContent>
          <OptionSection>
            <OptionBox onClick={() => handleOptionClick('enabled')} selected={selectedOption === 'enabled'}>
              <RadioButton selected={selectedOption === 'enabled'}>
                {selectedOption === 'enabled' && <RadioButtonInner />}
              </RadioButton>
              <OptionTextGroup>
                <OptionTitle>{t('Security.duplicateLogin.option.enabled.title')}</OptionTitle>
                <OptionDescription>{t('Security.duplicateLogin.option.enabled.description')}</OptionDescription>
              </OptionTextGroup>
            </OptionBox>

            <OptionBox onClick={() => handleOptionClick('disabled')} selected={selectedOption === 'disabled'}>
              <RadioButton selected={selectedOption === 'disabled'}>
                {selectedOption === 'disabled' && <RadioButtonInner />}
              </RadioButton>
              <OptionTextGroup>
                <OptionTitle>{t('Security.duplicateLogin.option.disabled.title')}</OptionTitle>
                <OptionDescription>{t('Security.duplicateLogin.option.disabled.description')}</OptionDescription>
              </OptionTextGroup>
            </OptionBox>
          </OptionSection>
        </DialogContent>

        <DialogFooter>
          <CustomButton variant="outline" size="extraLarge" onClick={handleClose} paddingHorizontal={24}>
            {t('GNB_Profile_MyProfile_11')}
          </CustomButton>
          <CustomSecureButton isPending={isPending} variant="contain" size="extraLarge" onClick={handleSave} paddingHorizontal={24}>
            {t('Common.button.save')}
          </CustomSecureButton>
        </DialogFooter>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 480px;
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border-radius: 6px;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);
  outline: none;
`;

const DialogHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 18px 28px;
  border-bottom: none;
`;

const HeaderTitle = styled(Typography)`
  ${fonts.Headline6}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const DialogContent = styled(Box)`
  padding: 14px 40px 0px 24px;
`;

const OptionSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OptionBox = styled(Box)<{ selected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  }
`;

const RadioButton = styled(Box)<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: ${({ selected, theme }) =>
    selected ? `1px solid ${theme.colors['border-primary']}` : `1px solid ${theme.colors['border-gray-light']}`};
  background-color: ${({ theme }) => theme.colors['bg-white']};
  flex-shrink: 0;
`;

const RadioButtonInner = styled(Box)`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors['bg-primary']};
`;

const OptionTextGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const OptionTitle = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const OptionDescription = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  white-space: pre-line;
  line-height: 1.4285714285714286em;
` as typeof Typography;

const DialogFooter = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px 68px;
  margin-top: 17px;
`;

const CustomButton = styled(Button)`
  width: 168px;
`;

const CustomSecureButton = styled(SecureSubmitButton)`
  width: 168px;
`;
